import React, { useCallback, useEffect, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import './StudentTests.css';

// Official (assigned) assessments — kept visibly apart from practice. Pending = not started or in progress,
// Completed = submitted. Everything shown is read straight from /portal/tests.
const scoreTone = (score, total) => {
  if (!total) return 'neutral';
  const p = (score / total) * 100;
  return p >= 80 ? 'success' : p >= 60 ? 'warning' : 'danger';
};

export const reviewPath = (test) => `/student/test/${test.id}/review`;

const StudentTests = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('pending');

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get('/portal/tests');
      setTests(res.data || []);
    } catch (err) {
      console.error('fetchTests error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const pending = tests.filter(t => t.attempt_status === 'in_progress' || t.attempt_status === 'not_started');
  const completed = tests.filter(t => t.attempt_status === 'submitted');

  if (loading) {
    return <div className="z-page z-page--narrow" role="status" aria-label="Loading assessments"><SkeletonCard lines={3} /><SkeletonCard lines={3} /></div>;
  }
  if (error) {
    return <div className="z-page z-page--narrow"><ErrorState error={error} onRetry={fetchTests} /></div>;
  }

  const sections = [
    { key: 'pending', label: 'Pending', count: pending.length },
    { key: 'completed', label: 'Completed', count: completed.length },
  ];

  return (
    <div className="z-page z-page--narrow st-page">
      <p className="st-intro">Assessments assigned to you by your college. These are official and scored.</p>

      <div className="z-segments" role="tablist" aria-label="Assessments">
        {sections.map(s => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={activeSection === s.key}
            className="z-segment"
            onClick={() => setActiveSection(s.key)}
          >
            {s.label} <span className="z-counter">{s.count}</span>
          </button>
        ))}
      </div>

      {activeSection === 'pending' && (
        pending.length === 0 ? (
          <EmptyState icon="test" art={false} tone="neutral" title="Nothing pending" body="No assessments are waiting for you right now. Keep practising while you wait." action={{ label: 'Go to Practice', onClick: () => navigate('/student/practice'), secondary: true }} />
        ) : (
          <ul className="st-list">
            {pending.map(t => (
              <li key={t.id} className="z-surface st-card">
                <div className="st-card-top">
                  {t.test_type && <span className="z-chip z-chip--violet st-type">{t.test_type}</span>}
                  {t.attempt_status === 'in_progress' && <span className="z-chip z-chip--warning">In progress</span>}
                </div>
                <div className="st-name">{t.test_name}</div>
                <div className="st-meta">
                  <span><Icon name="timer" size={14} /> {t.duration_minutes} mins</span>
                  <span><Icon name="test" size={14} /> {t.total_marks} marks</span>
                  {t.negative_marking && <span title="Each wrong answer deducts 1/4 of a question's marks. Unanswered questions lose nothing."><Icon name="warning" size={14} /> Negative marking (−¼ per wrong)</span>}
                </div>
                <button type="button" className="z-btn z-btn--primary z-btn--block" onClick={() => navigate(`/student/test/${t.id}`)}>
                  {t.attempt_status === 'in_progress' ? 'Continue' : 'Start test'} <Icon name="arrow-right" size={18} />
                </button>
              </li>
            ))}
          </ul>
        )
      )}

      {activeSection === 'completed' && (
        completed.length === 0 ? (
          <EmptyState icon="test" art={false} tone="neutral" title="No completed assessments yet" body="Your results and answer reviews will appear here after you submit one." />
        ) : (
          <ul className="st-list">
            {completed.map(t => {
              const pct = t.total_marks > 0 ? ((t.score / t.total_marks) * 100).toFixed(1) : null;
              return (
                <li key={t.id} className="z-surface st-card">
                  <div className="st-name">{t.test_name}</div>
                  <div className="st-score">
                    <span className={`z-chip z-chip--${scoreTone(t.score, t.total_marks)}`}>{t.score} / {t.total_marks}{pct != null ? ` · ${pct}%` : ''}</span>
                  </div>
                  <div className="st-breakdown">
                    <span className="st-b st-b--correct"><Icon name="success" size={14} /> {t.correct || 0} correct</span>
                    <span className="st-b st-b--wrong"><Icon name="error" size={14} /> {t.wrong || 0} wrong</span>
                    <span className="st-b st-b--skipped"><Icon name="skipped" size={14} /> {t.unattempted || 0} skipped</span>
                  </div>
                  <button type="button" className="z-btn z-btn--secondary z-btn--block" onClick={() => navigate(reviewPath(t))}>
                    <Icon name="eye" size={18} /> Review answers
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
};

export default StudentTests;
